import {
  isChatStatusBusy,
  type Thread,
  useSDK,
  useStores,
} from '@littlebird/sdk';
import { type SectionProps } from '@radix-ui/themes';
import { useNavigate, useParams } from '@tanstack/react-router';
import { range } from 'lodash';
import { observer } from 'mobx-react-lite';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
} from 'react';
import { toast } from 'sonner';
import { VList, type VListHandle } from 'virtua';

import { Icons } from '@/assets/icons/icons';
import { HiddenFromUserIcon } from '@/components/blocks/HiddenFromUserIcon';
import { Button, CoachMark, XStack, YStack } from '@/components/ui';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { RoundIconButton } from '@/design-system-v2/round-icon-button';
import { SidebarRow } from '@/design-system-v2/sidebar-row';
import { getArchiveErrorDescription } from '@/features/chat/archive-error';
import { useChatListHiddenChatIds } from '@/features/chat/chat-list-hidden-chats';
import {
  getChatScrollRestorationId,
  prewarmThreadSyntaxHighlights,
} from '@/features/chat/chat-syntax-highlight-prewarm';
import { chatStore, ThreadBackgroundState } from '@/features/chat/chat.store';
import {
  ghostChatsState,
  GhostChatVListItem,
} from '@/features/chat/ghost-chats';
import { firstRunSummaryStore } from '@/features/context-collection';
import { withoutHiddenAgentChats } from '@/features/home/proactive-agent-visibility';
import { ImChannelChatBadge } from '@/features/msg-channels/common/im-channel-chat-badge';
import { navRailStore } from '@/features/nav-rail/nav-rail.store';
import { webOnboardingThreadsStore } from '@/features/onboarding-web';
import {
  MoveToProjectDropdownSubMenu,
  MoveToProjectSubMenu,
} from '@/features/projects/move-to-project';
import {
  isProjectSidebarListItem,
  ProjectSidebarHeaderRow,
  ProjectSidebarVListItem,
} from '@/features/projects/projects';
import { useChatProjectDropTarget } from '@/features/projects/use-chat-project-drop';
import { FavoriteRoutineSidebarRow } from '@/features/routines/components/routine-chat-sidebar-section';
import { confirmChatListDeletion } from '@/features/settings/proactive-agent-chat-deletion';
import { useHiddenProactiveAgentChatIds } from '@/features/settings/proactive-agent.query';
import { isNewTabClick, useTabs } from '@/features/tabs';
import { tabStore } from '@/features/tabs/tabs.store';
import { TeamChatSidebarItem } from '@/features/team-chats/team-chat-sidebar-item';
import { useTeamChatRows } from '@/features/team-chats/use-team-chat-rows';
import { useAnimationFrameScheduler } from '@/hooks/useAnimationFrameScheduler';
import { useCallbackRef } from '@/hooks/useCallbackRef';
import { cn } from '@/lib/utils';
import { useDrawerNavigation } from '@/providers/DrawerNavigationProvider';
import { Panel, uiStore } from '@/stores/ui.store';
import { trackEvent } from '@/utils/analytics';
import {
  beginChatDrag,
  isChatDrag,
  scrollChatDragList,
} from '@/utils/chat-drag';
import { classList } from '@/utils/cn';

import {
  buildProjectSidebarSections,
  type CollapsibleSidebarSection,
  recencyLabel,
  type SidebarListItem as SectionListItem,
  threadItemKey,
} from './ChatSection.items';
import { SidebarListItemFrame } from './SidebarListItemFrame';
import { SidebarMoreRow } from './SidebarMoreRow';
import { SidebarSectionHeading } from './SidebarSectionContainer';
import { SidebarSectionRow } from './SidebarSectionRow';
import {
  findFirstVisibleSidebarSection,
  findSidebarSectionBoundaryIndexes,
  type SidebarSectionName,
} from './sticky-section';
import { StickySidebarSectionContext } from './sticky-section-context';
import { StickySectionHeading } from './StickySectionHeading';
import { SECTION_GAP } from './style';

// the gap after a section is padding on its boundary element, so the
// rect already spans it and the label flips exactly when the next
// heading arrives
const measureSectionBoundaries = (sidebarRoot: HTMLElement) =>
  Array.from(
    sidebarRoot.querySelectorAll<HTMLElement>('[data-sidebar-section-boundary]')
  ).map((element) => ({
    bottom: element.getBoundingClientRect().bottom,
    section: element.dataset.sidebarSectionBoundary as SidebarSectionName,
  }));

type SidebarSlotItem = {
  id: 'after-projects-content';
  type: 'slot';
  section: 'routines';
  content: React.ReactNode;
};

type SidebarListItem = SectionListItem | SidebarSlotItem;

type ChatSectionProps = SectionProps & {
  afterProjectsContent?: React.ReactNode;
};

type ChatSidebarSectionHeadingProps = {
  className?: string;
  hasFollowingSection?: boolean;
  isExpanded: boolean;
  isPinned?: boolean;
  isSectionBoundary?: boolean;
  isSticky?: boolean;
  onExpandedChange: (isExpanded: boolean) => void;
  onNavigate?: React.MouseEventHandler<HTMLButtonElement>;
  rightSlot?: React.ReactNode;
  section: Exclude<CollapsibleSidebarSection, 'projects'>;
  title?: string;
};

function ChatSidebarSectionHeading({
  className,
  hasFollowingSection = false,
  isExpanded,
  isPinned = false,
  isSectionBoundary = false,
  isSticky = false,
  onExpandedChange,
  onNavigate,
  rightSlot,
  section,
  title,
}: ChatSidebarSectionHeadingProps) {
  return (
    <SidebarSectionHeading
      aria-hidden={!isSticky && isPinned ? true : undefined}
      inert={!isSticky && isPinned ? true : undefined}
      data-sidebar-section-boundary={isSectionBoundary ? section : undefined}
      data-sidebar-section-heading={isSticky ? undefined : section}
      className={cn(
        !isSticky && 'mb-0.5',
        hasFollowingSection && ['mb-0', SECTION_GAP],
        className,
        !isSticky && isPinned && 'invisible'
      )}
      isExpanded={isExpanded}
      onExpandedChange={onExpandedChange}
      onNavigate={onNavigate}
      rightSlot={rightSlot}
    >
      {title ?? (section === 'favorites' ? 'Favorites' : 'Chats')}
    </SidebarSectionHeading>
  );
}

const findThreadItemKey = (
  items: SidebarListItem[],
  threadId: string | null | undefined
) => {
  if (!threadId) return null;

  for (const item of items) {
    if (
      !isProjectSidebarListItem(item) &&
      item.type === 'thread' &&
      item.id === threadId
    ) {
      return threadItemKey(item);
    }
  }
  return null;
};

function EmptyProjectChatItem({
  section,
  projectId,
  title,
  isSectionBoundary,
  hasFollowingSection,
}: {
  section: SidebarSectionName;
  projectId: string;
  title: string;
  isSectionBoundary: boolean;
  hasFollowingSection: boolean;
}) {
  const { isDragOver, dropTargetProps } = useChatProjectDropTarget(projectId);

  return (
    <SidebarListItemFrame
      section={section}
      isSectionBoundary={isSectionBoundary}
      hasFollowingSection={hasFollowingSection}
    >
      <SidebarRow.Root
        interactive={false}
        muted
        selected={isDragOver}
        className="text-xs"
        {...dropTargetProps}
      >
        <SidebarRow.Leading />
        {title}
      </SidebarRow.Root>
    </SidebarListItemFrame>
  );
}

function ProjectChatDropTarget({
  projectId,
  children,
}: {
  projectId: string;
  children: React.ReactNode;
}) {
  const { isDragOver, dropTargetProps } = useChatProjectDropTarget(projectId);

  return (
    <div
      className={cn(isDragOver && '[&_[role=button]]:hover:bg-transparent')}
      data-chat-drop-over={isDragOver ? '' : undefined}
      {...dropTargetProps}
    >
      {children}
    </div>
  );
}

function ShowMoreChatsItem({
  section,
  isSectionBoundary,
  hasFollowingSection,
  onShowMore,
}: {
  section: SidebarSectionName;
  isSectionBoundary: boolean;
  hasFollowingSection: boolean;
  onShowMore: React.MouseEventHandler<HTMLButtonElement>;
}) {
  return (
    <SidebarListItemFrame
      section={section}
      isSectionBoundary={isSectionBoundary}
      hasFollowingSection={hasFollowingSection}
    >
      <SidebarMoreRow className={undefined} onClick={onShowMore} />
    </SidebarListItemFrame>
  );
}

const ChatSectionImpl: React.FC<ChatSectionProps> = ({
  afterProjectsContent,
}) => {
  const navigate = useNavigate();
  const { openTab } = useTabs();
  const { setIsOpen } = useDrawerNavigation();

  const { messages, projects, routines, threads } = useStores();
  const hiddenChatIds = useHiddenProactiveAgentChatIds();
  const hiddenListedChatIds = useChatListHiddenChatIds();
  const pinnedChats = useMemo(
    () =>
      withoutHiddenAgentChats(
        threads.pinnedChats,
        hiddenListedChatIds,
        (thread) => thread.id
      ),
    [threads.pinnedChats, hiddenListedChatIds]
  );
  const recentChats = useMemo(
    () =>
      withoutHiddenAgentChats(
        threads.recentChats,
        hiddenListedChatIds,
        (thread) => thread.id
      ),
    [threads.recentChats, hiddenListedChatIds]
  );
  const favoriteRoutineIds = useMemo(
    () =>
      withoutHiddenAgentChats(
        routines.starredRoutines,
        hiddenChatIds,
        (routine) => routine.createdInChatId
      ).map((routine) => routine.referenceId),
    [routines.starredRoutines, hiddenChatIds]
  );
  const hasFavorites =
    pinnedChats.length > 0 ||
    projects.pinnedProjects.length > 0 ||
    favoriteRoutineIds.length > 0;
  const groupByRecency = navRailStore.groupChatsByRecency;
  const collapsedGroupsKey = [...navRailStore.collapsedChatGroups].join('|');
  const recentsTitle =
    groupByRecency && recentChats.length > 0
      ? recencyLabel(recentChats[0].lastMessageAt, new Date())
      : undefined;
  const { chatId } = useParams({ strict: false });
  const sidebarRootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<VListHandle>(null);
  const handleChatDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    if (!isChatDrag(event.dataTransfer)) return;
    event.preventDefault();
    const listElement = sidebarRootRef.current?.querySelector(
      '[data-sidebar-list]'
    );
    if (!listElement) return;
    scrollChatDragList(
      event,
      listRef.current,
      listElement.getBoundingClientRect().top
    );
  }, []);
  const [stickyHeaderPortalTarget, setStickyHeaderPortalTarget] =
    useState<HTMLDivElement | null>(null);
  const firstSidebarSection: SidebarSectionName = hasFavorites
    ? 'favorites'
    : 'projects';
  const [stickySection, setStickySection] = useState<SidebarSectionName | null>(
    firstSidebarSection
  );
  const expandedSections = uiStore.sidebarExpandedSections;
  const expandedProjectIdList = uiStore.sidebarExpandedProjectIds;
  const expandedProjectIds = useMemo(
    () => new Set(expandedProjectIdList),
    [expandedProjectIdList]
  );
  const loadProjectChats = useCallback(
    (projectId: string) => {
      void threads
        .loadProjectThreads(projectId)
        .catch(() => toast.error('Failed to load project chats'));
    },
    [threads]
  );
  const toggleProjectExpanded = useCallback(
    (projectId: string) => {
      const isExpanded = expandedProjectIds.has(projectId);
      uiStore.setSidebarProjectExpanded(projectId, !isExpanded);
      if (!isExpanded) loadProjectChats(projectId);
    },
    [expandedProjectIds, loadProjectChats]
  );
  const setSectionExpanded = useCallback(
    (section: CollapsibleSidebarSection, isExpanded: boolean) => {
      uiStore.setSidebarSectionExpanded(section, isExpanded);
    },
    []
  );

  const isRecentsExpanded = recentsTitle
    ? !navRailStore.collapsedChatGroups.has(recentsTitle)
    : expandedSections.recents;
  const toggleRecents = (isExpanded: boolean) =>
    recentsTitle
      ? navRailStore.toggleChatGroup(recentsTitle)
      : setSectionExpanded('recents', isExpanded);

  useEffect(() => {
    const thread = threads.get(chatId ?? '');
    if (thread) {
      trackEvent('CHAT_PAGE_VIEWED', {
        pagename: `/chats/${thread.id}`,
      });
    } else {
      trackEvent('CHAT_HOME_VIEWED', {
        pagename: '/chats',
      });
    }
  }, [chatId, threads]);

  const [clickedRow, setClickedRow] = useState<{
    chatId: string;
    itemKey: string;
  } | null>(null);
  useEffect(() => {
    setClickedRow((row) => (row?.chatId === chatId ? row : null));
  }, [chatId]);
  const selectChat = useCallbackRef(
    (thread: Thread, e?: React.MouseEvent, itemKey?: string) => {
      setIsOpen(false);
      if (e && isNewTabClick(e)) {
        openTab({ path: `/chats/${thread.id}` });
      } else {
        setClickedRow(itemKey ? { chatId: thread.id, itemKey } : null);
        navigate({ to: `/chats/${thread.id}` });
      }
    }
  );

  const handleOpenChatInTab = useCallbackRef((thread: Thread) => {
    openTab({ path: `/chats/${thread.id}` });
  });

  const handleSeeChats = useCallbackRef(
    (filter: 'all' | 'favorites', e?: React.MouseEvent) => {
      trackEvent('ALL_CHATS_SEE_ALL_CLICKED');
      setIsOpen(false);
      const search = filter === 'favorites' ? { filter } : undefined;
      if (e && isNewTabClick(e)) {
        openTab({ path: '/chats/all', search });
      } else {
        navigate({ to: '/chats/all', search: search ?? {} });
      }
    }
  );

  const handleSeeProjectChats = useCallbackRef(
    (projectId: string, event: React.MouseEvent) => {
      setIsOpen(false);
      if (isNewTabClick(event)) {
        openTab({ path: `/projects/${projectId}` });
      } else {
        navigate({ to: '/projects/$projectId', params: { projectId } });
      }
    }
  );

  const handlePrewarmChat = useCallbackRef((thread: Thread) => {
    const scrollRestorationId = getChatScrollRestorationId(
      tabStore.activeTabId,
      thread.id
    );

    const prewarm = () => {
      prewarmThreadSyntaxHighlights(
        thread.id,
        messages.getMessagesByThreadId(thread.id),
        { scrollRestorationId }
      );
    };

    prewarm();

    void threads
      .ensureThreadIsReady(thread.id)
      .then(prewarm)
      .catch(() => {});
  });

  const handleRenameChat = useCallbackRef(
    (thread: Thread, newTitle: string) => {
      threads.updateThread(thread, { title: newTitle });
    }
  );

  const handleFavoriteChat = useCallbackRef((thread: Thread) => {
    threads.updateThread(thread, { starred: !thread.starred });
  });

  const handleDeleteChat = useCallbackRef(async (thread: Thread) => {
    if (!(await confirmChatListDeletion([thread.id]))) return;
    const deletion = threads.delete(thread);
    if (chatId === thread.id) navigate({ to: '/chats' });
    try {
      await deletion;
    } catch (error) {
      toast.error('Failed to delete chat', {
        description: getArchiveErrorDescription(error, 'delete'),
      });
    }
  });

  const handleArchiveChat = useCallbackRef(async (thread: Thread) => {
    try {
      await threads.archiveThread(thread);
      trackEvent('CHAT_ARCHIVED', { chatId: thread.id });
      if (!uiStore.hasSeenArchivedChatsHint) {
        uiStore.setHasSeenArchivedChatsHint(true);
        toast.info('Chat archived', {
          description: 'You can restore chats anytime from Settings',
          duration: 10000,
        });
      }
      if (chatId === thread.id) navigate({ to: '/chats' });
    } catch (error) {
      toast.error('Failed to archive chat', {
        description: getArchiveErrorDescription(error, 'archive'),
      });
    }
  });

  const ghostItems = ghostChatsState.sidebarItems;
  const sidebarChatIds = useMemo(
    () => new Set([...pinnedChats, ...recentChats].map((thread) => thread.id)),
    [pinnedChats, recentChats]
  );

  const firstRunChatId =
    firstRunSummaryStore.phase === 'ready' ? firstRunSummaryStore.chatId : null;

  const webOnboardingChatId = webOnboardingThreadsStore.coachMarkThreadId;
  const requestedCoachMarkChatId =
    webOnboardingChatId && sidebarChatIds.has(webOnboardingChatId)
      ? webOnboardingChatId
      : firstRunChatId && sidebarChatIds.has(firstRunChatId)
        ? firstRunChatId
        : null;

  const focusedProjectId = threads.get(
    requestedCoachMarkChatId ?? chatId ?? ''
  )?.projectId;
  useEffect(() => {
    if (!focusedProjectId) return;
    uiStore.setSidebarProjectExpanded(focusedProjectId, true);
    loadProjectChats(focusedProjectId);
  }, [focusedProjectId, loadProjectChats]);
  const teamChats = useTeamChatRows(expandedProjectIds);
  const sidebarSections = useMemo(
    () =>
      buildProjectSidebarSections({
        teamChats,
        favoriteProjects: projects.pinnedProjects,
        projects: projects.recentProjects,
        favoriteRoutineIds,
        pinnedChats,
        recentChats,
        ghostItems,
        expandedProjectIds,
        hasMoreChats: threads.hasMorePages,
        groupByRecency,
        collapsedGroups: new Set(collapsedGroupsKey.split('|')),
      }),
    [
      ghostItems,
      projects.pinnedProjects,
      projects.recentProjects,
      favoriteRoutineIds,
      pinnedChats,
      recentChats,
      groupByRecency,
      collapsedGroupsKey,
      expandedProjectIds,
      threads.hasMorePages,
      teamChats,
    ]
  );

  const sidebarItems: SidebarListItem[] = useMemo(() => {
    const recentItems =
      expandedSections.recents || recentsTitle
        ? sidebarSections.recents
        : sidebarSections.recents.slice(0, 1);

    const slot = (
      id: SidebarSlotItem['id'],
      section: SidebarSlotItem['section'],
      content: React.ReactNode
    ): SidebarSlotItem[] =>
      content ? [{ id, type: 'slot', section, content }] : [];

    return [
      ...(expandedSections.favorites
        ? sidebarSections.favorites
        : sidebarSections.favorites.slice(0, 1)),
      ...(expandedSections.projects
        ? sidebarSections.projects
        : sidebarSections.projects.slice(0, 1)),
      ...slot('after-projects-content', 'routines', afterProjectsContent),
      ...recentItems,
    ];
  }, [afterProjectsContent, expandedSections, sidebarSections, recentsTitle]);
  const coachMarkItemKey = useMemo(
    () => findThreadItemKey(sidebarItems, requestedCoachMarkChatId),
    [requestedCoachMarkChatId, sidebarItems]
  );
  const selectedItemKey = useMemo(() => {
    const clickedItemKey = clickedRow?.itemKey;
    const isClickedRowVisible = sidebarItems.some(
      (item) =>
        !isProjectSidebarListItem(item) &&
        item.type === 'thread' &&
        item.id === chatId &&
        threadItemKey(item) === clickedItemKey
    );

    return clickedItemKey && isClickedRowVisible
      ? clickedItemKey
      : findThreadItemKey(sidebarItems, chatId);
  }, [chatId, clickedRow, sidebarItems]);
  const coachMarkChatId = coachMarkItemKey ? requestedCoachMarkChatId : null;
  const showWebOnboardingCoachMark =
    !!coachMarkChatId && coachMarkChatId === webOnboardingChatId;
  const showFirstRunCoachMark =
    !!coachMarkChatId &&
    !showWebOnboardingCoachMark &&
    coachMarkChatId === firstRunChatId;
  const virtualItemCount = sidebarItems.length;
  const sectionBoundaryIndexes = useMemo(
    () =>
      findSidebarSectionBoundaryIndexes(sidebarItems, (item) =>
        isProjectSidebarListItem(item) ? 'projects' : item.section
      ),
    [sidebarItems]
  );
  const sectionBoundaryIndexSet = useMemo(
    () => new Set(sectionBoundaryIndexes),
    [sectionBoundaryIndexes]
  );

  const updateStickySection = useCallback(() => {
    const list = listRef.current;
    const sidebarRoot = sidebarRootRef.current;
    const stickyHeadingRect = sidebarRoot
      ?.querySelector('[data-testid="sticky-sidebar-section-heading"]')
      ?.getBoundingClientRect();
    if (!list || !sidebarRoot || !stickyHeadingRect) return;

    const isPinned = list.scrollOffset > 0;
    const boundaries = measureSectionBoundaries(sidebarRoot);
    // slot content can render nothing, so the first section is read from
    // the DOM rather than from the props
    const nextStickySection = isPinned
      ? findFirstVisibleSidebarSection({
          boundaryTop: stickyHeadingRect.bottom,
          boundaries,
        })
      : (boundaries[0]?.section ?? null);

    sidebarRoot.style.setProperty(
      '--sidebar-list-cover',
      `${nextStickySection ? stickyHeadingRect.height : 0}px`
    );
    sidebarRoot.style.setProperty(
      '--sidebar-list-fade',
      isPinned ? '2rem' : '0px'
    );
    setStickySection((currentSection) =>
      currentSection === nextStickySection ? currentSection : nextStickySection
    );
  }, []);

  const scheduleStickySectionUpdate =
    useAnimationFrameScheduler(updateStickySection);

  const handleScroll = useCallback(() => {
    scheduleStickySectionUpdate();
  }, [scheduleStickySectionUpdate]);

  useEffect(() => {
    scheduleStickySectionUpdate();
  }, [sidebarItems, scheduleStickySectionUpdate]);

  const keepMounted = useMemo(
    () =>
      [
        ...new Set([
          ...range(Math.max(0, virtualItemCount - 10), virtualItemCount),
          ...sectionBoundaryIndexes,
        ]),
      ].sort((a, b) => a - b),
    [sectionBoundaryIndexes, virtualItemCount]
  );

  const hasTrackedFirstRunCoachMark = useRef(false);
  useEffect(() => {
    if (showFirstRunCoachMark && !hasTrackedFirstRunCoachMark.current) {
      hasTrackedFirstRunCoachMark.current = true;
      trackEvent('ONBOARDING_CONTEXT_FIRST_RUN_SUMMARY_VIEWED');
    }
  }, [showFirstRunCoachMark]);

  const handleCoachMarkOpenChange = useCallback(
    (next: boolean) => {
      if (next) return;
      if (showFirstRunCoachMark) {
        trackEvent('ONBOARDING_CONTEXT_FIRST_RUN_DISMISSED');
        firstRunSummaryStore.markDone();
        return;
      }
      webOnboardingThreadsStore.dismissThreadsCoachMark();
    },
    [showFirstRunCoachMark]
  );

  useEffect(() => {
    if (!showWebOnboardingCoachMark || !webOnboardingChatId) return;

    const index = sidebarItems.findIndex(
      (item) =>
        !isProjectSidebarListItem(item) &&
        item.type === 'thread' &&
        item.id === webOnboardingChatId
    );
    if (index < 0) return;

    listRef.current?.scrollToIndex(index, {
      align: 'center',
    });
  }, [showWebOnboardingCoachMark, sidebarItems, webOnboardingChatId]);

  const stickySectionContextValue = useMemo(
    () => ({
      onSectionLayoutChange: scheduleStickySectionUpdate,
      section: stickySection,
      portalTarget: stickyHeaderPortalTarget,
    }),
    [scheduleStickySectionUpdate, stickyHeaderPortalTarget, stickySection]
  );

  const newChatButton = (
    <>
      <RoundIconButton
        label="Open all chats"
        icon={Icons.arrowUpRight}
        size="xs"
        className="-my-0.5"
        onClick={(event) => handleSeeChats('all', event)}
      />
      <ChatViewOptionsMenu />
    </>
  );

  const stickySectionHeadings = {
    projects: (
      <YStack className="no-record">
        <ProjectSidebarHeaderRow
          hasProjects={projects.orderedData.length > 0}
          isExpanded={expandedSections.projects}
          onExpandedChange={(isExpanded) =>
            setSectionExpanded('projects', isExpanded)
          }
          onSelect={(event) => {
            setIsOpen(false);
            if (isNewTabClick(event)) {
              openTab({ path: '/projects' });
            } else {
              navigate({ to: '/projects' });
            }
          }}
        />
      </YStack>
    ),
    favorites: (
      <ChatSidebarSectionHeading
        section="favorites"
        isSticky
        isExpanded={expandedSections.favorites}
        onExpandedChange={(isExpanded) =>
          setSectionExpanded('favorites', isExpanded)
        }
      />
    ),
    recents: (
      <ChatSidebarSectionHeading
        section="recents"
        title={recentsTitle}
        isSticky
        isExpanded={isRecentsExpanded}
        onExpandedChange={toggleRecents}
        onNavigate={
          recentsTitle ? undefined : (event) => handleSeeChats('all', event)
        }
        rightSlot={newChatButton}
      />
    ),
  };

  return (
    <StickySidebarSectionContext.Provider value={stickySectionContextValue}>
      <YStack
        ref={sidebarRootRef}
        className="group/drawer w-full h-full overflow-hidden"
        onDragOver={handleChatDragOver}
      >
        <YStack className="relative flex-1">
          <CoachMark.Root
            open={!!coachMarkChatId}
            onOpenChange={handleCoachMarkOpenChange}
          >
            <VList
              ref={listRef}
              data-sidebar-list
              className={classList(
                'sidebar-list',
                'scrollbar-hide',
                'touch-pan-y',
                'h-full',
                'pb-4'
              )}
              overscan={10}
              keepMounted={keepMounted}
              onScroll={handleScroll}
            >
              {sidebarItems.map((item, index) => {
                const hasFollowingSection =
                  sectionBoundaryIndexSet.has(index) &&
                  index < sidebarItems.length - 1;
                if (isProjectSidebarListItem(item)) {
                  const isExpanded = expandedProjectIds.has(item.id);
                  const canExpand = item.type === 'project';

                  return (
                    <ProjectSidebarVListItem
                      key={item.id}
                      item={item}
                      isSectionBoundary={sectionBoundaryIndexSet.has(index)}
                      hasFollowingSection={hasFollowingSection}
                      isProjectsPinned={stickySection === 'projects'}
                      isProjectsExpanded={expandedSections.projects}
                      onProjectsExpandedChange={(isExpanded) =>
                        setSectionExpanded('projects', isExpanded)
                      }
                      isExpanded={isExpanded}
                      onToggleExpanded={
                        canExpand
                          ? () => toggleProjectExpanded(item.id)
                          : undefined
                      }
                    />
                  );
                }

                if (item.type === 'slot') {
                  return <Fragment key={item.id}>{item.content}</Fragment>;
                }

                if (item.type === 'favorite-project') {
                  return (
                    <ProjectSidebarVListItem
                      key={item.id}
                      item={{ id: item.id, type: 'project' }}
                      section="favorites"
                      isSectionBoundary={sectionBoundaryIndexSet.has(index)}
                      hasFollowingSection={hasFollowingSection}
                      isProjectsPinned={stickySection === 'projects'}
                      isProjectsExpanded={expandedSections.projects}
                      onProjectsExpandedChange={(isExpanded) =>
                        setSectionExpanded('projects', isExpanded)
                      }
                      isExpanded={expandedProjectIds.has(item.id)}
                      onToggleExpanded={() => toggleProjectExpanded(item.id)}
                    />
                  );
                }

                if (item.type === 'favorite-routine') {
                  return (
                    <SidebarListItemFrame
                      key={item.id}
                      section="favorites"
                      isSectionBoundary={sectionBoundaryIndexSet.has(index)}
                      hasFollowingSection={hasFollowingSection}
                    >
                      <FavoriteRoutineSidebarRow routineId={item.routineId} />
                    </SidebarListItemFrame>
                  );
                }

                if (item.type === 'header') {
                  const { section } = item;

                  return (
                    <ChatSidebarSectionHeading
                      key={item.id}
                      section={section}
                      hasFollowingSection={hasFollowingSection}
                      title={section === 'recents' ? recentsTitle : undefined}
                      isExpanded={
                        section === 'recents'
                          ? isRecentsExpanded
                          : expandedSections[section]
                      }
                      isPinned={stickySection === section}
                      isSectionBoundary={sectionBoundaryIndexSet.has(index)}
                      onExpandedChange={(isExpanded) =>
                        section === 'recents'
                          ? toggleRecents(isExpanded)
                          : setSectionExpanded(section, isExpanded)
                      }
                      rightSlot={
                        section === 'recents' ? newChatButton : undefined
                      }
                      onNavigate={
                        section === 'recents' && !recentsTitle
                          ? (event) => handleSeeChats('all', event)
                          : undefined
                      }
                    />
                  );
                }

                if (item.type === 'recency-label') {
                  return (
                    <SidebarListItemFrame
                      key={item.id}
                      section="recents"
                      isSectionBoundary={sectionBoundaryIndexSet.has(index)}
                      hasFollowingSection={hasFollowingSection}
                    >
                      <RecencyGroupLabel
                        label={item.label}
                        isExpanded={
                          !navRailStore.collapsedChatGroups.has(item.label)
                        }
                      />
                    </SidebarListItemFrame>
                  );
                }

                if (item.type === 'ghost') {
                  return (
                    <GhostChatVListItem
                      key={item.id}
                      item={item}
                      isSectionBoundary={sectionBoundaryIndexSet.has(index)}
                      hasFollowingSection={hasFollowingSection}
                    />
                  );
                }

                if (item.type === 'projects-in-favorites') {
                  return (
                    <SidebarListItemFrame
                      key={item.id}
                      section="projects"
                      isSectionBoundary={sectionBoundaryIndexSet.has(index)}
                      hasFollowingSection={hasFollowingSection}
                    >
                      <SidebarRow.Root
                        interactive={false}
                        muted
                        className="text-xs"
                      >
                        All projects are in Favorites
                      </SidebarRow.Root>
                    </SidebarListItemFrame>
                  );
                }

                if (item.type === 'team-chat') {
                  return (
                    <TeamChatSidebarItem
                      key={item.id}
                      section={item.section}
                      projectId={item.projectId}
                      chatId={item.chat.id}
                      title={item.chat.title}
                      posters={item.chat.posters}
                      unread={item.chat.unread}
                      isSectionBoundary={sectionBoundaryIndexSet.has(index)}
                      hasFollowingSection={hasFollowingSection}
                    />
                  );
                }

                if (item.type === 'empty-project') {
                  return (
                    <EmptyProjectChatItem
                      key={item.id}
                      section={item.section}
                      projectId={item.projectId}
                      title={item.title}
                      isSectionBoundary={sectionBoundaryIndexSet.has(index)}
                      hasFollowingSection={hasFollowingSection}
                    />
                  );
                }

                if (item.type === 'show-more-project-chats') {
                  return (
                    <ShowMoreChatsItem
                      key={item.id}
                      section={item.section}
                      isSectionBoundary={sectionBoundaryIndexSet.has(index)}
                      hasFollowingSection={hasFollowingSection}
                      onShowMore={(event) =>
                        handleSeeProjectChats(item.projectId, event)
                      }
                    />
                  );
                }

                if (item.type === 'show-more-chats') {
                  return (
                    <ShowMoreChatsItem
                      key={item.id}
                      section="recents"
                      isSectionBoundary={sectionBoundaryIndexSet.has(index)}
                      hasFollowingSection={hasFollowingSection}
                      onShowMore={(event) => handleSeeChats('all', event)}
                    />
                  );
                }

                const thread = threads.get(item.id);
                if (!thread) return null;
                const itemKey = threadItemKey(item);
                const rowContent = (
                  <SidebarListItemFrame
                    key={itemKey}
                    section={item.section}
                    isSectionBoundary={sectionBoundaryIndexSet.has(index)}
                    hasFollowingSection={hasFollowingSection}
                    data-testid={`chat-${item.id}`}
                    data-selected={
                      itemKey === selectedItemKey ? 'true' : undefined
                    }
                  >
                    <SectionItem
                      thread={thread}
                      itemKey={itemKey}
                      isSelected={itemKey === selectedItemKey}
                      isInProject={'projectId' in item}
                      isFavorite={
                        item.section === 'favorites' && !('projectId' in item)
                      }
                      selectChat={selectChat}
                      onOpenInTab={handleOpenChatInTab}
                      onPrewarm={handlePrewarmChat}
                      onRename={handleRenameChat}
                      onFavorite={handleFavoriteChat}
                      onArchive={handleArchiveChat}
                      onDelete={handleDeleteChat}
                    />
                  </SidebarListItemFrame>
                );
                const row =
                  'projectId' in item ? (
                    <ProjectChatDropTarget
                      key={itemKey}
                      projectId={item.projectId}
                    >
                      {rowContent}
                    </ProjectChatDropTarget>
                  ) : (
                    rowContent
                  );
                if (itemKey === coachMarkItemKey) {
                  return (
                    <CoachMark.Anchor key={itemKey} asChild>
                      {row}
                    </CoachMark.Anchor>
                  );
                }
                return row;
              })}
            </VList>
            <StickySectionHeading
              headings={stickySectionHeadings}
              section={stickySection}
              onPortalTargetChange={setStickyHeaderPortalTarget}
            />
            {showFirstRunCoachMark && (
              <CoachMark.Content
                side="right"
                align="start"
                sideOffset={12}
                width="sm"
              >
                <CoachMark.Title>
                  Here's what Littlebird learned
                </CoachMark.Title>
                <CoachMark.Description>
                  We turned what Littlebird picked up about your work into a
                  chat. Open it whenever you like.
                </CoachMark.Description>
                <CoachMark.Footer>
                  <CoachMark.Dismiss asChild>
                    <Button size="sm">Got it</Button>
                  </CoachMark.Dismiss>
                </CoachMark.Footer>
              </CoachMark.Content>
            )}
            {showWebOnboardingCoachMark && (
              <CoachMark.Content
                side="right"
                align="start"
                sideOffset={12}
                width="sm"
              >
                <CoachMark.Title>Littlebird is on it</CoachMark.Title>
                <CoachMark.Description>
                  {webOnboardingThreadsStore.threadIds.length > 1
                    ? 'These chats are looking into what you asked for. Open them to see what Littlebird found.'
                    : 'This chat is looking into what you asked for. Open it to see what Littlebird found.'}
                </CoachMark.Description>
                <CoachMark.Footer>
                  <CoachMark.Dismiss asChild>
                    <Button size="sm">Got it</Button>
                  </CoachMark.Dismiss>
                </CoachMark.Footer>
              </CoachMark.Content>
            )}
          </CoachMark.Root>
        </YStack>
      </YStack>
    </StickySidebarSectionContext.Provider>
  );
};

export const ChatSection = observer(ChatSectionImpl);

const RecencyGroupLabel = ({
  label,
  isExpanded,
}: {
  label: string;
  isExpanded: boolean;
}) => (
  <button
    type="button"
    aria-expanded={isExpanded}
    className="group/recency flex items-center gap-1 px-[var(--row-pad)] pb-1 pt-5 text-left text-sm text-foreground-muted transition-colors hover:text-foreground focus-ring"
    onClick={() => navRailStore.toggleChatGroup(label)}
  >
    {label}
    <Icons.chevronDown
      aria-hidden
      className={cn(
        'size-3.5 shrink-0 opacity-0 transition-[opacity,transform] duration-150 group-hover/recency:opacity-100',
        !isExpanded && '-rotate-90 opacity-100'
      )}
    />
  </button>
);

const ChatViewOptionsMenu = observer(() => (
  <DropdownMenu.Root>
    <DropdownMenu.Trigger asChild>
      <RoundIconButton
        label="View options"
        icon={Icons.slidersHorizontal}
        size="xs"
        className="-my-0.5"
      />
    </DropdownMenu.Trigger>
    <DropdownMenu.Content align="end" className="w-48">
      <DropdownMenu.Label>Group chats by</DropdownMenu.Label>
      <DropdownMenu.RadioGroup
        value={navRailStore.groupChatsByRecency ? 'recency' : 'none'}
        onValueChange={(value) =>
          navRailStore.setGroupChatsByRecency(value === 'recency')
        }
      >
        <DropdownMenu.RadioItem value="recency">Recency</DropdownMenu.RadioItem>
        <DropdownMenu.RadioItem value="none">None</DropdownMenu.RadioItem>
      </DropdownMenu.RadioGroup>
    </DropdownMenu.Content>
  </DropdownMenu.Root>
));

const SectionItem: React.FC<{
  thread: Thread;
  itemKey: string;
  isSelected: boolean;
  isInProject: boolean;
  isFavorite: boolean;
  selectChat: (thread: Thread, e?: React.MouseEvent, itemKey?: string) => void;
  onOpenInTab: (thread: Thread) => void;
  onPrewarm: (thread: Thread) => void;
  onRename: (thread: Thread, newTitle: string) => void;
  onFavorite: (thread: Thread) => void;
  onArchive: (thread: Thread) => void;
  onDelete: (thread: Thread) => void;
}> = observer(
  ({
    thread,
    itemKey,
    isSelected,
    isInProject,
    isFavorite,
    onDelete,
    onFavorite,
    onArchive,
    onOpenInTab,
    onPrewarm,
    onRename,
    selectChat,
  }) => {
    const backgroundState = chatStore.getThreadBackgroundStatus(thread.id);
    const { voiceMode } = useSDK();
    const isVoiceModeActive = voiceMode.isActiveForDraft(thread.id);
    const isGenerating =
      isChatStatusBusy(thread.status) ||
      backgroundState === ThreadBackgroundState.BackgroundWithActivity;

    const isActive =
      backgroundState === ThreadBackgroundState.BackgroundWithActivityNotified;

    const contextMenuExtra = useMemo(
      () => <MoveToProjectSubMenu threadId={thread.id} />,
      [thread.id]
    );
    const dropdownMenuExtra = useMemo(
      () => <MoveToProjectDropdownSubMenu threadId={thread.id} />,
      [thread.id]
    );

    return (
      <SidebarSectionRow
        item={thread}
        title={thread.title}
        isSelected={isSelected}
        onSelect={(selectedThread, event) =>
          selectChat(selectedThread, event, itemKey)
        }
        onOpenInTab={onOpenInTab}
        onPrewarm={onPrewarm}
        onRename={onRename}
        onFavorite={onFavorite}
        onMarkUnread={
          isActive ? undefined : () => chatStore.markThreadUnread(thread.id)
        }
        onArchive={onArchive}
        onDelete={onDelete}
        isFavorited={thread.starred}
        contextMenuExtra={contextMenuExtra}
        dropdownMenuExtra={dropdownMenuExtra}
        panel={Panel.Chats}
        draggable
        onDragStart={(event) => beginChatDrag(event, thread.id)}
        showLeading={isInProject || isFavorite}
        leftSide={isFavorite ? <SidebarRow.Icon icon={Icons.speech} /> : null}
        rightSide={
          isVoiceModeActive ||
          thread.hiddenFromUser ||
          isActive ||
          thread.isMessagingChannel ? (
            <XStack className="items-center gap-1">
              <ImChannelChatBadge visible={thread.isMessagingChannel} />
              <HiddenFromUserIcon hidden={thread.hiddenFromUser} />
              {isVoiceModeActive && (
                <>
                  <Icons.dictation
                    aria-label="Voice mode active"
                    className="size-3 text-foreground-muted"
                    strokeWidth={2}
                  />
                </>
              )}
              {isActive && <SidebarRow.StatusDot state="unread" />}
            </XStack>
          ) : null
        }
        isLoading={isGenerating}
      />
    );
  }
);
